import React from 'react';
import DsLogo from './DsLogo';
import DropdownProfile from '../../../components/header/dropdown/profile';
import './CompleteProfileForm.css';

// AccountSetup's focused shell and form, backed by the main application's
// profile state. The payout field remains part of the existing API contract.
//
// The header (the same sticky ink band CompleteProfile always had) stays
// exactly as-is — only the form body below it was restyled to the
// reference's open ledger grammar: a serif section heading over an ink
// rule, then rows directly on the canvas (Klargo-Reference/src/pages/
// AccountSetup.tsx), matching how Profile already states the same facts.

/** Serif section heading over an ink rule; the rows follow directly on the canvas. */
const Section = ({ title, notes, children }) => (
  <section className="mt-[34px]">
    <div className="border-b-2 border-ds-ink pb-2">
      <h2 className="ds-title-component m-0">{title}</h2>
    </div>
    <div>{children}</div>
    {notes?.map((n, i) => (
      <p key={i} className={`ds-meta m-0 max-w-[58ch] ${i === 0 ? 'mt-2.5' : 'mt-1.5'}`}>{n}</p>
    ))}
  </section>
);

/** A fact row: label left, value right — what BankID already knows, stated once. */
const FactRow = ({ label, value, mono }) => (
  <div className="flex min-h-[52px] items-center justify-between gap-4 border-b border-ds-rule-row py-3">
    <span className="ds-label shrink-0">{label}</span>
    <span className={`min-w-0 truncate text-right text-[15px] font-medium text-ds-ink ${mono ? 'ds-mono text-[14px] tracking-[0.04em] tabular-nums' : ''}`}>
      {value}
    </span>
  </div>
);

/** A field row: the same ledger row, with a control where the value would be. */
const FieldRow = ({ id, label, error, children }) => (
  <div className="flex flex-col gap-1.5 border-b border-ds-rule-row py-3 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
    <label htmlFor={id} className="ds-label shrink-0 sm:pt-[13px]">{label}</label>
    <div className="w-full sm:max-w-[336px]">
      {children}
      {error && <p id={`${id}-error`} role="alert" className="ds-error-text m-0 mt-2">{error}</p>}
    </div>
  </div>
);

/** One statement to tick — the licence attestation and the terms take the same row. */
const StatementRow = ({ id, checked, onChange, note, children }) => (
  <div className="border-b border-ds-rule-row py-4">
    <div className="flex items-start gap-3">
      <input
        id={id}
        type="checkbox"
        checked={checked}
        onChange={e => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 shrink-0 rounded border-ds-rule-edge"
        style={{ accentColor: 'var(--ds-green)' }}
      />
      <label htmlFor={id} className="cursor-pointer text-[15px] leading-[1.5] text-ds-ink">{children}</label>
    </div>
    {note && <p className="ds-meta m-0 ml-[30px] mt-2 max-w-[58ch]">{note}</p>}
  </div>
);

const LEGAL_LINK = 'ds-underline font-semibold text-ds-ink transition-colors hover:text-ds-green';

/** The same masked treatment Profile gives the personnummer. */
const maskPersonalId = (ssn) => {
  if (!ssn) return '—';
  const digits = ssn.replace(/\D/g, '');
  return digits.length >= 8 ? `${digits.slice(0, 8)}-****` : '—';
};

export default function CompleteProfileForm({
  t, pageLoading, saving, isBankId, bankIdSSN, form, headerDisplayName,
  termsAccepted, setTermsAccepted, emailValid, phoneValid,
  setField, onEmailChange, onPhoneChange, handleSubmit, onVerifyBankId,
}) {
  // Same form for both sign-ups; only the e-mail is optional for a BankID account.
  const emailLabel = isBankId ? `${t('accountSetup.email')} ${t('accountSetup.optional')}` : t('accountSetup.email');

  return (
    <div className="complete-profile ds-scope ds-form flex min-h-screen min-w-[320px] flex-col bg-ds-canvas font-ds-sans text-ds-body">
      <header className="sticky top-0 z-50 bg-ds-ink text-ds-ink-on-soft" style={{ boxShadow: '0 1px 0 rgba(0,0,0,.25)' }}>
        <div className="ds-auth-rail flex h-[60px] items-center gap-[clamp(22px,3vw,40px)]">
          <span className="flex items-center gap-2 px-0.5 py-3" role="img" aria-label="Klargo"><DsLogo /></span>
          <div style={{ marginLeft: 'auto' }}>
            {/* This screen sits between BankID/signup and the Overview: not
                yet a place to move around in, so the header withholds the
                account panel entirely (Klargo-Reference AuthShell.tsx's
                "focused" mode) and leaves only the language control. */}
            <DropdownProfile focused hideAccount identityName={headerDisplayName} />
          </div>
        </div>
      </header>
      <main id="main-content" className="w-full flex-[1_1_auto]" aria-busy={pageLoading}>
        {pageLoading ? (
          <div className="ds-auth-rail max-w-[680px] pt-[var(--ds-page-top)]" role="status">
            <p className="ds-body">{t('accountSetup.loading')}</p>
          </div>
        ) : (
          <div className="ds-animate-page ds-auth-rail max-w-[680px] pb-[var(--ds-page-bottom)] pt-[var(--ds-page-top)]">
            <div className="mb-2 text-[13px] font-medium text-ds-muted">{t('accountSetup.eyebrow')}</div>
            <h1 className="ds-title-route m-0">{t('accountSetup.completeTitle')}</h1>
            <p className="ds-body m-0 mt-3 max-w-[54ch]">
              {t(isBankId ? 'accountSetup.bankIdVerified' : 'accountSetup.completeSubtitle')}
            </p>

            <form onSubmit={handleSubmit} noValidate aria-busy={saving}>
              {/* Personuppgifter — the same rows for both sign-ups. A BankID account's name comes from
                  BankID, so its name fields are filled in and locked (and its personnummer is shown). */}
              <Section
                title={t('accountSetup.personalInfo')}
                notes={[t('accountSetup.contactPurpose'), ...(isBankId ? [t('profile.bankidInfo')] : [])]}
              >
                <FieldRow id="firstName" label={t('accountSetup.firstName')}>
                  <input
                    id="firstName"
                    className="ds-input read-only:cursor-default read-only:bg-ds-canvas"
                    autoComplete="given-name"
                    placeholder={t('accountSetup.firstNamePlaceholder')}
                    value={form.firstName ?? ''}
                    readOnly={isBankId}
                    onChange={e => setField('firstName', e.target.value)}
                  />
                </FieldRow>
                <FieldRow id="lastName" label={t('accountSetup.lastName')}>
                  <input
                    id="lastName"
                    className="ds-input read-only:cursor-default read-only:bg-ds-canvas"
                    autoComplete="family-name"
                    placeholder={t('accountSetup.lastNamePlaceholder')}
                    value={form.lastName ?? ''}
                    readOnly={isBankId}
                    onChange={e => setField('lastName', e.target.value)}
                  />
                </FieldRow>
                {isBankId && bankIdSSN && (
                  <FactRow label={t('accountSetup.personalId')} value={maskPersonalId(bankIdSSN)} mono />
                )}
                {onVerifyBankId && (
                  <FieldRow id="bankId" label={t('accountSetup.bankIdLabel')}>
                    <button
                      id="bankId"
                      type="button"
                      onClick={onVerifyBankId}
                      disabled={saving}
                      className="ds-input w-full text-left font-semibold text-ds-ink disabled:opacity-60"
                    >
                      {t('verify.verifyWithBankId')} {t('accountSetup.optional')}
                    </button>
                    <p className="ds-meta m-0 mt-2">{t('accountSetup.bankIdLater')}</p>
                  </FieldRow>
                )}
                <FieldRow
                  id="phoneNumber"
                  label={t('accountSetup.phone')}
                  error={phoneValid === false ? t('accountSetup.phoneInvalid') : undefined}
                >
                  <input
                    id="phoneNumber"
                    type="tel"
                    inputMode="tel"
                    autoComplete="tel"
                    placeholder="+46 70 123 45 67"
                    className="ds-input tabular-nums"
                    value={form.phoneNumber ?? ''}
                    onChange={e => onPhoneChange(e.target.value)}
                    aria-invalid={phoneValid === false || undefined}
                    aria-describedby={phoneValid === false ? 'phoneNumber-error' : undefined}
                  />
                </FieldRow>
                <FieldRow
                  id="email"
                  label={emailLabel}
                  error={emailValid === false ? t('accountSetup.emailInvalid') : undefined}
                >
                  <input
                    id="email"
                    type="email"
                    inputMode="email"
                    autoComplete="email"
                    autoCapitalize="none"
                    spellCheck={false}
                    placeholder="your@email.com"
                    className="ds-input"
                    value={form.email ?? ''}
                    onChange={e => onEmailChange(e.target.value)}
                    aria-invalid={emailValid === false || undefined}
                    aria-describedby={emailValid === false ? 'email-error' : undefined}
                  />
                </FieldRow>
              </Section>

              {/* Körkort — a self-attestation to tick, nothing collected. */}
              <Section title={t('profile.section.license')}>
                <StatementRow
                  id="hasValidLicense"
                  checked={form.hasValidLicense === true}
                  onChange={v => setField('hasValidLicense', v)}
                  note={`${t('profile.licenseHint')} ${t('accountSetup.license.later')}`}
                >
                  {t('profile.licenseCheckbox')}
                </StatementRow>
              </Section>

              {/* Villkor — the required consent, in the same row grammar. */}
              <Section title={t('accountSetup.section.terms')}>
                <StatementRow id="terms" checked={termsAccepted} onChange={setTermsAccepted}>
                  {t('accountSetup.termsPrefix')}{' '}
                  <a href="/villkor" target="_blank" rel="noopener noreferrer" className={LEGAL_LINK}>
                    {t('accountSetup.termsLink')}
                  </a>{' '}
                  {t('accountSetup.termsAnd')}{' '}
                  <a href="/integritet" target="_blank" rel="noopener noreferrer" className={LEGAL_LINK}>
                    {t('accountSetup.privacyLink')}
                  </a>
                </StatementRow>
              </Section>

              <div className="mt-8 flex flex-col items-stretch sm:flex-row sm:items-center">
                <button
                  type="submit"
                  disabled={saving || !termsAccepted}
                  className="ds-action-ink w-full justify-center disabled:cursor-not-allowed disabled:opacity-60 sm:w-auto sm:min-w-[220px]"
                >
                  {t(saving ? 'accountSetup.creating' : 'accountSetup.createAccount')}
                </button>
              </div>
            </form>
          </div>
        )}
      </main>
    </div>
  );
}
